import React from "react";
import styled from "styled-components";
import {
  colors2,
  LoaderOverlay,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION,
  UI_CAPTION_SEMIBOLD,
  UI_TAG,
} from "oolib";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import { getTenant } from "@/TENANT_CONFIGS";
import { getIntegrationIcon, DefaultIconFallback } from "../IntegrationInterfaces";
import { CsvExcelIcon } from "../DataSourceSelector";
import { UploadButtonAndPreview } from "@/pageTpls/PlatformBuilder/comps/BatchRegisterPage/comps/UploadButtonAndPreview";

/**
 * Source-selection step of the compact import flow (body only — rendered inside
 * the shared ImportModalShell). The CSV/Excel card routes through
 * `onSelectSource("csvExcelUpload")` — the add-data step then renders the
 * standard UploadButtonAndPreview dropzone. Every other card is built from the
 * BE master config (platformConfigs._DirectDataImport.EXTERNAL_IMPORT_ADAPTERS_CONFIG),
 * filtered through the same prod feature-flag check as DataSourceSelector, and
 * also routes through `onSelectSource(id)`.
 *
 * Grouping is derived from authType (no dedicated config field): platformManaged
 * sources need no setup, so they go under "Other options"; everything the user
 * connects/authenticates to goes under "Connect your data".
 */
const SECTIONS = [
  { key: "connect", label: "Connect your data" },
  { key: "other", label: "Other options" },
];

const getCategory = (adapter) =>
  adapter.authType === "platformManaged" ? "other" : "connect";

const renderBrandIcon = (id, name) => {
  const Icon = getIntegrationIcon(id);
  return Icon ? <Icon size={24} /> : <DefaultIconFallback name={name} />;
};

// Sizes/colors icons drawn with currentColor and no intrinsic size (CsvExcelIcon).
const IconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${colors2.grey60};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  width: 100%;
`;

// Equal-width cards, 4 per row, wrapping — so a growing adapter list stays tidy
// (the last row is left-aligned instead of stretching to fill).
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
`;

const Card = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px;
  height: ${({ $tall }) => ($tall ? "107px" : "88px")};
  background: ${colors2.grey5};
  border: 1px solid ${colors2.grey10};
  border-radius: 12px;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${colors2.grey40};
    background: ${colors2.grey10};
  }
`;

export const SourceSelectionView = ({
  onSelectSource,
  onFileSelect,
  isUploading,
  // Dropzone only, no integration cards.
  hideExternalSources = false,
}) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const allAdapters =
    platformConfigs?._DirectDataImport?.EXTERNAL_IMPORT_ADAPTERS_CONFIG || [];

  // Same filter as DataSourceSelector: drop adapters disabled on prod, using the
  // tenant-aware helper so per-tenant whitelist entries are honoured.
  const isProd = import.meta.env.VITE_ENV === "prod";
  const disabledFeatures = platformConfigs?.FEATURES_DISABLED_ON_PROD || [];
  const externalAdapters = allAdapters.filter(
    (a) =>
      !isFeatureDisabledOnProd(`${a.id}Integration`, {
        disabledFeatures,
        isProd,
        tenant: getTenant(),
      })
  );

  return (
    <>
      {isUploading && <LoaderOverlay loaderText="Processing file..." />}
      {/* Previously: click this card -> navigate to add-data step -> click dropzone.
          Now the dropzone is rendered inline here so CSV/Excel upload is a single step.
          To revert: delete the <UploadButtonAndPreview .../> block below and
          un-comment this <Row> card. */}
      {/* <Row>
        <Card $tall onClick={() => onSelectSource("csvExcelUpload")}>
          <IconSlot>
            <CsvExcelIcon />
          </IconSlot>
          <UI_BODY_SEMIBOLD_SM_DF>Spreadsheet (CSV) / Excel</UI_BODY_SEMIBOLD_SM_DF>
          {/* <UI_CAPTION color={colors2.grey50}>
            Click to upload or drop a file here
          </UI_CAPTION> * /}
        </Card>
      </Row> */}
      <div style={{ width: "100%" }}>
        <UploadButtonAndPreview onFileSelect={onFileSelect} />
      </div>

      {!hideExternalSources && SECTIONS.map(({ key, label }) => {
        const adapters = externalAdapters.filter((a) => getCategory(a) === key);
        if (adapters.length === 0) return null;

        return (
          <Section key={key}>
            <UI_TAG color={colors2.grey50}>{label}</UI_TAG>
            <Grid>
              {adapters.map((a) => (
                <Card key={a.id} onClick={() => onSelectSource(a.id)}>
                  {renderBrandIcon(a.id, a.name)}
                  <UI_CAPTION_SEMIBOLD>{a.name}</UI_CAPTION_SEMIBOLD>
                </Card>
              ))}
            </Grid>
          </Section>
        );
      })}
    </>
  );
};

export default SourceSelectionView;
